import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Dropdown, Menu, Tooltip } from 'antd';
import moment from 'moment';
import cn from 'classnames';
import executeTimeActions from '../../../../../actions/execute-time.actions';
import { ignoredExecutionTime } from '../../../../../constants/ignoredExecutionTime';
import CellText from '../../../CellText/CellText';
import { ChangePriority } from '../../../RequestPriority/RequestPriority';
import styles from './ExecuteDateWithActions.module.scss';

const render = (request) => {
  const { taskStart, taskEnd, executeDateTime } = request || {};

  if (taskStart && taskEnd) {
    const start = moment(taskStart).format('HH:mm');
    const end = moment(taskEnd).format('HH:mm');
    const date = moment(taskStart).format('DD.MM.YYYY');

    return `${date} ${start} - ${end}`;
  } else if (executeDateTime) {
    const dateTimeInstance = moment(executeDateTime);

    const time = dateTimeInstance.format('HH:mm');
    const hoursFormat = ignoredExecutionTime.includes(time) ? '' : 'HH:mm';
    const dayFormat = 'DD.MM.YYYY';
    const format = [dayFormat, hoursFormat].filter(Boolean).join(' ');

    return dateTimeInstance.format(format);
  } else {
    return 'Не указано';
  }
};

const ExecuteDate = ({
  onClick,
  request,
  isActive,
  requestsPage = false,
  className,
  ...props
}) => {
  const Container = requestsPage ? 'span' : 'div';

  const content = render(request);

  return (
    <Container
      {...props}
      className={cn(styles.root, className, {
        [styles.active]: isActive,
        [styles.flex]: !requestsPage,
      })}
      onClick={onClick}
    >
      <svg className={cn(styles.icon, 'icon', 'icon-history')}>
        <use xlinkHref="#icon-history"></use>
      </svg>
      {requestsPage ? (
        <span className={cn({ 'dspx-ref': isActive })}>{content}</span>
      ) : (
        <CellText>{content}</CellText>
      )}
    </Container>
  );
};

const useChangeExecuteDate = (request) => {
  const { CanChangeExecuteDate } = useSelector(
    (state) => state.authentication.userInfo
  );
  const dispatch = useDispatch();

  const onClick = useCallback(
    (e) => {
      e.stopPropagation();
      dispatch(
        executeTimeActions.openExecuteTimeModal({
          rid: `${request.id}`,
          rData: request,
        })
      );
    },
    [dispatch, request]
  );

  return { isActive: CanChangeExecuteDate, onClick };
};

const ChangeExecuteDate = ({ request, requestsPage, canChange }) => {
  const { isActive: canExecuteDateChange, onClick } =
    useChangeExecuteDate(request);

  const isActive = canExecuteDateChange && canChange;

  return (
    <Tooltip
      title={isActive ? 'Изменить дату выполнения' : 'Плановая дата выполнения'}
      title={isActive ? 'Изменить дату назначения' : 'Дата назначения'}
      mouseLeaveDelay={0}
    >
      <ExecuteDate
        onClick={isActive ? onClick : undefined}
        request={request}
        isActive={isActive}
        requestsPage={requestsPage}
      />
    </Tooltip>
  );
};

const OverlayMenu = ({ request, onPriorityOk, ...props }) => {
  const { isActive, onClick } = useChangeExecuteDate(request);

  return (
    <Menu {...props}>
      {isActive && (
        <Menu.Item onClick={({ domEvent }) => onClick(domEvent)}>
          Изменить дату назначения
        </Menu.Item>
      )}
      <Menu.Item
        onClick={(e) => {
          e.domEvent.stopPropagation();
        }}
      >
        <ChangePriority
          record={request}
          onOk={onPriorityOk}
        >
          Выставить приоритет
        </ChangePriority>
      </Menu.Item>
    </Menu>
  );
};

const ExecuteDateDropdown = ({
  request,
  onPriorityOk,
  requestsPage,
  canChange,
}) => {
  if (!canChange) {
    return (
      <Tooltip title='Дата назначения'>
        <ExecuteDate
          isActive={false}
          request={request}
          requestsPage={requestsPage} />
      </Tooltip>
    );
  }

  return (
    <Tooltip title='Дата назначения'>
      <Dropdown
        overlay={
          <OverlayMenu
            request={request}
            onPriorityOk={onPriorityOk}
          />
        }
      >
        <ExecuteDate
          isActive
          request={request}
          requestsPage={requestsPage}
        />
      </Dropdown>
    </Tooltip>
  );
};

const ExecuteDateWithActions = ({
  request,
  onPriorityOk,
  requestsPage,
  isStatistics = false,
  canChange = true,
}) => {
  const hasPriority = Boolean(request.priority);

  return hasPriority || isStatistics ? (
    <ChangeExecuteDate
      request={request}
      requestsPage={requestsPage}
      canChange={canChange}
    />
  ) : (
    <ExecuteDateDropdown
      request={request}
      onPriorityOk={onPriorityOk}
      requestsPage={requestsPage}
      canChange={canChange}
    />
  );
};

export default ExecuteDateWithActions;
